import React, { useState, useEffect } from 'react';
import './JsonFieldEditor.css';

interface JsonField {
  key: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  label: string;
  description?: string;
  required?: boolean;
  defaultValue?: unknown;
  options?: { label: string; value: unknown }[];
  children?: JsonField[];
}

interface JsonFieldEditorProps {
  value: unknown;
  onChange: (value: unknown) => void;
  template: JsonField[];
  label?: string;
  description?: string;
  disabled?: boolean;
}

const JsonFieldEditor: React.FC<JsonFieldEditorProps> = ({
  value,
  onChange,
  template,
  label,
  description,
  disabled = false,
}) => {
  const [expandedFields, setExpandedFields] = useState<Record<string, boolean>>({});
  const [localValue, setLocalValue] = useState<Record<string, unknown>>(
    (value as Record<string, unknown>) || {}
  );

  useEffect(() => {
    setLocalValue((value as Record<string, unknown>) || {});
  }, [value]);

  const handleValueChange = (key: string, newValue: unknown) => {
    if (disabled) return;
    const updatedValue = { ...localValue, [key]: newValue };
    setLocalValue(updatedValue);
    onChange(updatedValue);
  };

  const toggleExpand = (key: string) => {
    if (disabled) return;
    setExpandedFields((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderField = (field: JsonField, parentKey = '') => {
    const fullKey = parentKey ? `${parentKey}.${field.key}` : field.key;
    const currentValue = localValue[field.key];
    const isExpanded = expandedFields[fullKey];

    const renderFieldInput = () => {
      switch (field.type) {
        case 'string':
          if (field.options) {
            return (
              <select
                value={(currentValue as string) || ''}
                onChange={(e) => handleValueChange(field.key, e.target.value)}
                className="json-field-select"
                disabled={disabled}
              >
                <option value="">Select {field.label}</option>
                {field.options.map((option) => (
                  <option key={String(option.value)} value={String(option.value)}>
                    {option.label}
                  </option>
                ))}
              </select>
            );
          }
          return (
            <input
              type="text"
              value={(currentValue as string) || ''}
              onChange={(e) => handleValueChange(field.key, e.target.value)}
              className="json-field-input"
              placeholder={`Enter ${field.label.toLowerCase()}`}
              disabled={disabled}
            />
          );

        case 'number':
          return (
            <input
              type="number"
              value={(currentValue as number) || ''}
              onChange={(e) => handleValueChange(field.key, Number(e.target.value))}
              className="json-field-input"
              placeholder={`Enter ${field.label.toLowerCase()}`}
              disabled={disabled}
            />
          );

        case 'boolean':
          return (
            <div className="json-field-boolean">
              <label>
                <input
                  type="checkbox"
                  checked={(currentValue as boolean) || false}
                  onChange={(e) => handleValueChange(field.key, e.target.checked)}
                  disabled={disabled}
                />
                {field.label}
              </label>
            </div>
          );

        case 'array':
          return (
            <div className="json-field-array">
              <button
                onClick={() => toggleExpand(fullKey)}
                className="json-field-expand-button"
                disabled={disabled}
              >
                {isExpanded ? '▼' : '▶'} {field.label} (
                {Array.isArray(currentValue) ? currentValue.length : 0} items)
              </button>
              {isExpanded && (
                <div className="json-field-array-items">
                  {Array.isArray(currentValue) ? (
                    currentValue.map((item, index) => (
                      <div key={index} className="json-field-array-item">
                        <input
                          type="text"
                          value={item as string}
                          onChange={(e) => {
                            const newArray = [...(currentValue as string[])];
                            newArray[index] = e.target.value;
                            handleValueChange(field.key, newArray);
                          }}
                          className="json-field-input"
                          disabled={disabled}
                        />
                        <button
                          onClick={() => {
                            const newArray = (currentValue as string[]).filter(
                              (_: string, i: number) => i !== index
                            );
                            handleValueChange(field.key, newArray);
                          }}
                          className="json-field-remove-button"
                          disabled={disabled}
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  ) : (
                    <div>No items</div>
                  )}
                  <button
                    onClick={() => {
                      const newArray = Array.isArray(currentValue)
                        ? [...(currentValue as string[]), '']
                        : [''];
                      handleValueChange(field.key, newArray);
                    }}
                    className="json-field-add-button"
                    disabled={disabled}
                  >
                    Add Item
                  </button>
                </div>
              )}
            </div>
          );

        case 'object':
          return (
            <div className="json-field-object">
              <button
                onClick={() => toggleExpand(fullKey)}
                className="json-field-expand-button"
                disabled={disabled}
              >
                {isExpanded ? '▼' : '▶'} {field.label}
              </button>
              {isExpanded && field.children && (
                <div className="json-field-object-content">
                  {field.children.map((child) => renderField(child, fullKey))}
                </div>
              )}
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div key={field.key} className="json-field">
        {field.type !== 'boolean' && (
          <label className="json-field-label">
            {field.label}
            {field.required && <span className="required">*</span>}
            {field.description && <span className="field-description">{field.description}</span>}
          </label>
        )}
        {renderFieldInput()}
      </div>
    );
  };

  return (
    <div className="json-field-editor">
      {label && <h3 className="json-field-editor-label">{label}</h3>}
      {description && <p className="json-field-editor-description">{description}</p>}
      <div className="json-field-editor-content">{template.map((field) => renderField(field))}</div>
      <div className="json-field-editor-raw">
        <button
          onClick={() => toggleExpand('raw')}
          className="json-field-expand-button"
          disabled={disabled}
        >
          {expandedFields['raw'] ? '▼' : '▶'} Raw JSON
        </button>
        {expandedFields['raw'] && (
          <textarea
            value={JSON.stringify(localValue, null, 2)}
            onChange={(e) => {
              try {
                const newValue = JSON.parse(e.target.value);
                setLocalValue(newValue);
                onChange(newValue);
              } catch (error) {
                // Invalid JSON, don't update
              }
            }}
            className="json-field-raw-input"
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
};

export default JsonFieldEditor;
